<template>
  <div class="team-tab">
    <CommonOverlay :show="busy" title="Loading Reports" message="Please wait while we fetch your custom reports..." />
    <h2 class="page-title">Team Insights - Custom Reports</h2>
    <b-card class="border-0 h-100">
      <div class="date-filter-container">
        <b-row class="date-filter-group" align-v="center">
          <!-- From Date Section -->
          <b-col cols="12" md="auto" class="date-filter">
            <label class="date-label">From:</label>
            <b-row class="date-inputs" align-v="center">
              <b-col>
                <div class="date-picker-wrapper">
                  <DatePicker lang="en" type="month" v-model="fromMonth" format="MMM" placeholder="Select month">
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>
              </b-col>
              <b-col>
                <div class="date-picker-wrapper">
                  <DatePicker lang="en" type="year" v-model="fromYear" format="YYYY" placeholder="Select Year">
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>
              </b-col>
            </b-row>
          </b-col>

          <!-- To Date Section -->
          <b-col cols="12" md="auto" class="date-filter">
            <label class="date-label">To:</label>
            <b-row class="date-inputs" align-v="center">
              <b-col>
                <div class="date-picker-wrapper">
                  <DatePicker lang="en" type="month" v-model="toMonth" format="MMM" placeholder="Select Month">
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>
              </b-col>
              <b-col>
                <div class="date-picker-wrapper">
                  <DatePicker lang="en" type="year" v-model="toYear" format="YYYY" placeholder="Select Year">
                    <i class="glyphicon glyphicon-th" slot="calendar-icon" />
                  </DatePicker>
                </div>
              </b-col>
            </b-row>
          </b-col>

          <!-- Apply Filter Button -->
          <b-col cols="12" md="auto" class="submit-button">
            <b-button variant="primary" size="sm" @click="applyFilter">
              <i class="fa fa-search mr-1"></i>
              Apply Filter
            </b-button>
          </b-col>
        </b-row>
      </div>
    </b-card>
    <b-row class="pt-5" v-if="filtersApplied">
      <b-col xl="12" xs="12">
        <!-- TAB PANEL START -->
        <section class="tab-panel">
          <!-- Overdue Recall Section -->
          <div class="info-section">
            <div class="row">
              <div class="col-sm-4 border-right">
                <h5 class="section-title">Overdue Recall</h5>
                <b-card class="background-cover"
                  v-if="((overdueRecallDataMonthly && overdueRecallDataMonthly.totalCalls > 0 && allSettings) || (overdueRecallScheduledDataMonthly && overdueRecallScheduledDataMonthly.length > 0 && allSettings))">
                  <p class="fs-mini fw-semi-bold">Total follow-ups made: <b-button id="odcalls-Monthly"
                      variant="default" class="mb-xs mr-xs btn-xs"><span class="fs-mini fw-semi-bold">{{
                        overdueRecallDataMonthly.totalCalls }} follow-up(s)</span></b-button></p>
                  <b-popover target="odcalls-Monthly" triggers="focus" placement="top"
                    v-if="overdueRecallTasksDataMonthly && overdueRecallTasksDataMonthly.length > 0">
                    <template v-slot:title><b>Follow-up(s) made</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Followed-up on</th>
                          <th><span class="glyphicon glyphicon-user mr-2 mb-1"></span>Team member</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in overdueRecallTasksDataMonthly"
                          :key="`od-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td>{{ appt.CreatedAt | dateformat(dateFormatStr) }}</td>
                          <td>{{ appt.Employee }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <p class="fs-mini fw-semi-bold">Total scheduled:
                    <b-button id="odscheduled-Monthly" variant="default" class="mb-xs mr-xs btn-xs">
                      <span
                        v-if="((overdueRecallScheduledDataMonthly && overdueRecallScheduledDataMonthly.length === 0) || (!overdueRecallScheduledDataMonthly))"
                        class="fs-mini fw-semi-bold">0 appointment(s)</span>
                      <span
                        v-else-if="overdueRecallScheduledDataMonthly && overdueRecallScheduledDataMonthly.length > 0"
                        class="fs-mini fw-semi-bold">{{ overdueRecallScheduledDataMonthly.length }}
                        appointment(s)</span>
                    </b-button>
                    {{ sumArrValue(overdueRecallScheduledDataMonthly, 'AppointmentValue') | money }}
                  </p>
                  <b-popover target="odscheduled-Monthly" triggers="focus" placement="top"
                    v-if="overdueRecallScheduledDataMonthly && overdueRecallScheduledDataMonthly.length > 0">
                    <template v-slot:title><b>Appointment(s) Scheduled</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Appointment Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in overdueRecallScheduledDataMonthly"
                          :key="`od-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td><span class="text-success fw-semi-bold">{{ appt.AppointmentValue |
                              money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <div class="chart-panel">
                    <InsightPieChart :chartdata="overdueRecallDataMonthly.chartData" :options="pieChartOptions" />
                  </div>
                </b-card>
                <b-card class="background-cover" v-else>
                  <div>
                    <img src="../../assets/notenoughdata-place-holder.png" alt="..." width="330" />
                  </div>
                </b-card>
              </div>
              <div class="col-sm-8" v-if="overdueRecallDataMonthlyByEmp && overdueRecallDataMonthlyByEmp.length > 0">
                <h5 class="section-title">Breakdown by Team member(s)</h5>
                <div class="team-member-grid">
                  <b-card class="background-cover team-member-card"
                    v-for="(employee, index) in overdueRecallDataMonthlyByEmp" :key="`employee-${index}`">
                    <div class="person-name">
                      <span class="glyphicon glyphicon-user mr-2"></span>
                      <div>
                        <h6>{{ employee.name }}</h6>
                        <p class="fw-semi-bold">{{ employee.totalCalls }} Follow-up(s)</p>
                      </div>
                    </div>
                    <div class="chart-panel">
                      <ChartJSBarChart :chartData="employee.chartData" :dateRange="formattedResultsStr"
                        :timeRange="timeRange" />
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
          </div>

          <!-- Unscheduled Treatment Section -->
          <div class="info-section">
            <div class="row">
              <div class="col-sm-4 border-right">
                <h5 class="section-title">Unscheduled Treatment</h5>
                <b-card class="background-cover"
                  v-if="((unscheduledTreatmentDataMonthly && unscheduledTreatmentDataMonthly.totalCalls > 0 && allSettings) || (unscheduledTreatmentScheduledDataMonthly && unscheduledTreatmentScheduledDataMonthly.length > 0 && allSettings))">
                  <p class="fs-mini fw-semi-bold">Total follow-ups made: <b-button id="utcalls-Monthly"
                      variant="default" class="mb-xs mr-xs btn-xs"><span class="fs-mini fw-semi-bold">{{
                        unscheduledTreatmentDataMonthly.totalCalls }} follow-up(s)</span></b-button></p>
                  <b-popover target="utcalls-Monthly" triggers="focus" placement="top"
                    v-if="unscheduledTreatmentTasksDataMonthly && unscheduledTreatmentTasksDataMonthly.length > 0">
                    <template v-slot:title><b>Follow-up(s) made</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Followed-up on</th>
                          <th><span class="glyphicon glyphicon-user mr-2 mb-1"></span>Team member</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in unscheduledTreatmentTasksDataMonthly"
                          :key="`ut-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td>{{ appt.CreatedAt | dateformat(dateFormatStr) }}</td>
                          <td>{{ appt.Employee }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <p class="fs-mini fw-semi-bold">Total scheduled:
                    <b-button id="utscheduled-Monthly" variant="default" class="mb-xs mr-xs btn-xs">
                      <span
                        v-if="((unscheduledTreatmentScheduledDataMonthly && unscheduledTreatmentScheduledDataMonthly.length === 0) || (!unscheduledTreatmentScheduledDataMonthly))"
                        class="fs-mini fw-semi-bold">0 appointment(s)</span>
                      <span
                        v-else-if="unscheduledTreatmentScheduledDataMonthly && unscheduledTreatmentScheduledDataMonthly.length > 0"
                        class="fs-mini fw-semi-bold">{{ unscheduledTreatmentScheduledDataMonthly.length }}
                        appointment(s)</span>
                    </b-button>
                    {{ sumArrValue(unscheduledTreatmentScheduledDataMonthly, 'AppointmentValue') | money }}
                  </p>
                  <b-popover target="utscheduled-Monthly" triggers="focus" placement="top"
                    v-if="unscheduledTreatmentScheduledDataMonthly && unscheduledTreatmentScheduledDataMonthly.length > 0">
                    <template v-slot:title><b>Appointment(s) Scheduled</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Appointment Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in unscheduledTreatmentScheduledDataMonthly"
                          :key="`ut-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td><span class="text-success fw-semi-bold">{{ appt.AppointmentValue |
                              money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <div class="chart-panel">
                    <InsightPieChart :chartdata="unscheduledTreatmentDataMonthly.chartData"
                      :options="pieChartOptions" />
                  </div>
                </b-card>
                <b-card class="background-cover" v-else>
                  <div>
                    <img src="../../assets/notenoughdata-place-holder.png" alt="..." width="330" />
                  </div>
                </b-card>
              </div>
              <div class="col-sm-8"
                v-if="unscheduledTreatmentDataMonthlyByEmp && unscheduledTreatmentDataMonthlyByEmp.length > 0">
                <h5 class="section-title">Breakdown by Team member(s)</h5>
                <div class="team-member-grid">
                  <b-card class="background-cover team-member-card"
                    v-for="(employee, index) in unscheduledTreatmentDataMonthlyByEmp" :key="`employee-${index}`">
                    <div class="person-name">
                      <span class="glyphicon glyphicon-user mr-2"></span>
                      <div>
                        <h6>{{ employee.name }}</h6>
                        <p class="fw-semi-bold">{{ employee.totalCalls }} Follow-up(s)</p>
                      </div>
                    </div>
                    <div class="chart-panel">
                      <ChartJSBarChart :chartData="employee.chartData" :dateRange="formattedResultsStr"
                        :timeRange="timeRange" />
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
          </div>

          <!-- Outstanding Claims Section -->
          <div class="info-section">
            <div class="row">
              <div class="col-sm-4 border-right">
                <h5 class="section-title">Outstanding Claims</h5>
                <b-card class="background-cover"
                  v-if="((outstandingClaimsDataMonthly && outstandingClaimsDataMonthly.totalCalls > 0 && allSettings) || (outstandingClaimsClaimReceivedDataMonthly && outstandingClaimsClaimReceivedDataMonthly.length > 0 && allSettings))">
                  <p class="fs-mini fw-semi-bold">Total follow-ups made: <b-button id="occalls-Monthly"
                      variant="default" class="mb-xs mr-xs btn-xs"><span class="fs-mini fw-semi-bold">{{
                        outstandingClaimsDataMonthly.totalCalls }} follow-up(s)</span></b-button></p>
                  <b-popover target="occalls-Monthly" triggers="focus" placement="top"
                    v-if="outstandingClaimsTasksDataMonthly && outstandingClaimsTasksDataMonthly.length > 0">
                    <template v-slot:title><b>Follow-up(s) made</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Followed-up on</th>
                          <th><span class="glyphicon glyphicon-user mr-2 mb-1"></span>Team member</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in outstandingClaimsTasksDataMonthly"
                          :key="`oc-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td>{{ appt.CreatedAt | dateformat(dateFormatStr) }}</td>
                          <td>{{ appt.Employee }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <p class="fs-mini fw-semi-bold">Total received:
                    <b-button id="ocreceived-Monthly" variant="default" class="mb-xs mr-xs btn-xs">
                      <span
                        v-if="((outstandingClaimsClaimReceivedDataMonthly && outstandingClaimsClaimReceivedDataMonthly.length === 0) || (!outstandingClaimsClaimReceivedDataMonthly))"
                        class="fs-mini fw-semi-bold">0 claim(s)</span>
                      <span
                        v-else-if="outstandingClaimsClaimReceivedDataMonthly && outstandingClaimsClaimReceivedDataMonthly.length > 0"
                        class="fs-mini fw-semi-bold">{{ outstandingClaimsClaimReceivedDataMonthly.length }}
                        claim(s)</span>
                    </b-button>
                    {{ sumArrValue(outstandingClaimsClaimReceivedDataMonthly, 'ClaimValue') | money }}
                  </p>
                  <b-popover target="ocreceived-Monthly" triggers="focus" placement="top"
                    v-if="outstandingClaimsClaimReceivedDataMonthly && outstandingClaimsClaimReceivedDataMonthly.length > 0">
                    <template v-slot:title><b>Claim(s) Received</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Claim Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in outstandingClaimsClaimReceivedDataMonthly"
                          :key="`oc-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td><span class="text-success fw-semi-bold">{{ appt.ClaimValue |
                              money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <div class="chart-panel">
                    <InsightPieChart :chartdata="outstandingClaimsDataMonthly.chartData" :options="pieChartOptions" />
                  </div>
                </b-card>
                <b-card class="background-cover" v-else>
                  <div>
                    <img src="../../assets/notenoughdata-place-holder.png" alt="..." width="330" />
                  </div>
                </b-card>
              </div>
              <div class="col-sm-8"
                v-if="outstandingClaimsDataMonthlyByEmp && outstandingClaimsDataMonthlyByEmp.length > 0">
                <h5 class="section-title">Breakdown by Team member(s)</h5>
                <div class="team-member-grid">
                  <b-card class="background-cover team-member-card"
                    v-for="(employee, index) in outstandingClaimsDataMonthlyByEmp" :key="`employee-${index}`">
                    <div class="person-name">
                      <span class="glyphicon glyphicon-user mr-2"></span>
                      <div>
                        <h6>{{ employee.name }}</h6>
                        <p class="fw-semi-bold">{{ employee.totalCalls }} Follow-up(s)</p>
                      </div>
                    </div>
                    <div class="chart-panel">
                      <ChartJSBarChart :chartData="employee.chartData" :dateRange="formattedResultsStr"
                        :timeRange="timeRange" />
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
          </div>

          <!-- Broken Appointment Section -->
          <div class="info-section">
            <div class="row">
              <div class="col-sm-4 border-right">
                <h5 class="section-title">Broken Appointment</h5>
                <b-card class="background-cover"
                  v-if="((brokenAppointmentDataMonthly && brokenAppointmentDataMonthly.totalCalls > 0 && allSettings) || (brokenAppointmentScheduledDataMonthly && brokenAppointmentScheduledDataMonthly.length > 0 && allSettings))">
                  <p class="fs-mini fw-semi-bold">Total follow-ups made: <b-button id="bacalls-Monthly"
                      variant="default" class="mb-xs mr-xs btn-xs"><span class="fs-mini fw-semi-bold">{{
                        brokenAppointmentDataMonthly.totalCalls }} follow-up(s)</span></b-button></p>
                  <b-popover target="bacalls-Monthly" triggers="focus" placement="top"
                    v-if="brokenAppointmentTasksDataMonthly && brokenAppointmentTasksDataMonthly.length > 0">
                    <template v-slot:title><b>Follow-up(s) made</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Followed-up on</th>
                          <th><span class="glyphicon glyphicon-user mr-2 mb-1"></span>Team member</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in brokenAppointmentTasksDataMonthly"
                          :key="`ba-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td>{{ appt.CreatedAt | dateformat(dateFormatStr) }}</td>
                          <td>{{ appt.Employee }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <p class="fs-mini fw-semi-bold">Total scheduled:
                    <b-button id="bascheduled-Monthly" variant="default" class="mb-xs mr-xs btn-xs">
                      <span
                        v-if="((brokenAppointmentScheduledDataMonthly && brokenAppointmentScheduledDataMonthly.length === 0) || (!brokenAppointmentScheduledDataMonthly))"
                        class="fs-mini fw-semi-bold">0 appointment(s)</span>
                      <span
                        v-else-if="brokenAppointmentScheduledDataMonthly && brokenAppointmentScheduledDataMonthly.length > 0"
                        class="fs-mini fw-semi-bold">{{ brokenAppointmentScheduledDataMonthly.length }}
                        appointment(s)</span>
                    </b-button>
                    {{ sumArrValue(brokenAppointmentScheduledDataMonthly, 'AppointmentValue') | money }}
                  </p>
                  <b-popover target="bascheduled-Monthly" triggers="focus" placement="top"
                    v-if="brokenAppointmentScheduledDataMonthly && brokenAppointmentScheduledDataMonthly.length > 0">
                    <template v-slot:title><b>Appointment(s) Scheduled</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Appointment Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in brokenAppointmentScheduledDataMonthly"
                          :key="`ba-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td><span class="text-success fw-semi-bold">{{ appt.AppointmentValue |
                              money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <div class="chart-panel">
                    <InsightPieChart :chartdata="brokenAppointmentDataMonthly.chartData" :options="pieChartOptions" />
                  </div>
                </b-card>
                <b-card class="background-cover" v-else>
                  <div>
                    <img src="../../assets/notenoughdata-place-holder.png" alt="..." width="330" />
                  </div>
                </b-card>
              </div>
              <div class="col-sm-8"
                v-if="brokenAppointmentDataMonthlyByEmp && brokenAppointmentDataMonthlyByEmp.length > 0">
                <h5 class="section-title">Breakdown by Team member(s)</h5>
                <div class="team-member-grid">
                  <b-card class="background-cover team-member-card"
                    v-for="(employee, index) in brokenAppointmentDataMonthlyByEmp" :key="`employee-${index}`">
                    <div class="person-name">
                      <span class="glyphicon glyphicon-user mr-2"></span>
                      <div>
                        <h6>{{ employee.name }}</h6>
                        <p class="fw-semi-bold">{{ employee.totalCalls }} Follow-up(s)</p>
                      </div>
                    </div>
                    <div class="chart-panel">
                      <ChartJSBarChart :chartData="employee.chartData" :dateRange="formattedResultsStr"
                        :timeRange="timeRange" />
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
          </div>

          <!-- Pre-authorizations Section -->
          <div class="info-section">
            <div class="row">
              <div class="col-sm-4 border-right">
                <h5 class="section-title">Pre-authorizations</h5>
                <b-card class="background-cover"
                  v-if="((preAuthorizationDataMonthly && preAuthorizationDataMonthly.totalCalls > 0 && allSettings) || (preAuthorizationScheduledDataMonthly && preAuthorizationScheduledDataMonthly.length > 0 && allSettings))">
                  <p class="fs-mini fw-semi-bold">Total follow-ups made: <b-button id="pacalls-Monthly"
                      variant="default" class="mb-xs mr-xs btn-xs"><span class="fs-mini fw-semi-bold">{{
                        preAuthorizationDataMonthly.totalCalls }} follow-up(s)</span></b-button></p>
                  <b-popover target="pacalls-Monthly" triggers="focus" placement="top"
                    v-if="preAuthorizationTasksDataMonthly && preAuthorizationTasksDataMonthly.length > 0">
                    <template v-slot:title><b>Follow-up(s) made</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Followed-up on</th>
                          <th><span class="glyphicon glyphicon-user mr-2 mb-1"></span>Team member</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in preAuthorizationTasksDataMonthly"
                          :key="`pa-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td>{{ appt.CreatedAt | dateformat(dateFormatStr) }}</td>
                          <td>{{ appt.Employee }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <p class="fs-mini fw-semi-bold">Total Received:
                    <b-button id="pascheduled-Monthly" variant="default" class="mb-xs mr-xs btn-xs">
                      <span
                        v-if="((preAuthorizationScheduledDataMonthly && preAuthorizationScheduledDataMonthly.length === 0) || (!preAuthorizationScheduledDataMonthly))"
                        class="fs-mini fw-semi-bold">0 appointment(s)</span>
                      <span
                        v-else-if="preAuthorizationScheduledDataMonthly && preAuthorizationScheduledDataMonthly.length > 0"
                        class="fs-mini fw-semi-bold">{{ preAuthorizationScheduledDataMonthly.length }}
                        appointment(s)</span>
                    </b-button>
                    {{ sumArrValue(preAuthorizationScheduledDataMonthly, 'AppointmentValue') | money }}
                  </p>
                  <b-popover target="pascheduled-Monthly" triggers="focus" placement="top"
                    v-if="preAuthorizationScheduledDataMonthly && preAuthorizationScheduledDataMonthly.length > 0">
                    <template v-slot:title><b>Appointment(s) Scheduled</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Appointment Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in preAuthorizationScheduledDataMonthly"
                          :key="`pa-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td><span class="text-success fw-semi-bold">{{ appt.AppointmentValue |
                              money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <div class="chart-panel">
                    <InsightPieChart :chartdata="preAuthorizationDataMonthly.chartData" :options="pieChartOptions" />
                  </div>
                </b-card>
                <b-card class="background-cover" v-else>
                  <div>
                    <img src="../../assets/notenoughdata-place-holder.png" alt="..." width="330" />
                  </div>
                </b-card>
              </div>
              <div class="col-sm-8"
                v-if="preAuthorizationDataMonthlyByEmp && preAuthorizationDataMonthlyByEmp.length > 0">
                <h5 class="section-title">Breakdown by Team member(s)</h5>
                <div class="team-member-grid">
                  <b-card class="background-cover team-member-card"
                    v-for="(employee, index) in preAuthorizationDataMonthlyByEmp" :key="`employee-${index}`">
                    <div class="person-name">
                      <span class="glyphicon glyphicon-user mr-2"></span>
                      <div>
                        <h6>{{ employee.name }}</h6>
                        <p class="fw-semi-bold">{{ employee.totalCalls }} Follow-up(s)</p>
                      </div>
                    </div>
                    <div class="chart-panel">
                      <ChartJSBarChart :chartData="employee.chartData" :dateRange="formattedResultsStr"
                        :timeRange="timeRange" />
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
          </div>

          <!-- Patient Balances Section -->
          <div class="info-section">
            <div class="row">
              <div class="col-sm-4 border-right">
                <h5 class="section-title">Patient Balances</h5>
                <b-card class="background-cover"
                  v-if="((patientBalancesDataMonthly && patientBalancesDataMonthly.totalCalls > 0 && allSettings))">
                  <p class="fs-mini fw-semi-bold">Total follow-ups made: <b-button id="pbcalls-Monthly"
                      variant="default" class="mb-xs mr-xs btn-xs"><span class="fs-mini fw-semi-bold">{{
                        patientBalancesDataMonthly.totalCalls }} follow-up(s)</span></b-button></p>
                  <b-popover target="pbcalls-Monthly" triggers="focus" placement="top"
                    v-if="patientBalancesTasksDataMonthly && patientBalancesTasksDataMonthly.length > 0">
                    <template v-slot:title><b>Follow-up(s) made</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Followed-up on</th>
                          <th><span class="glyphicon glyphicon-user mr-2 mb-1"></span>Team member</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in patientBalancesTasksDataMonthly"
                          :key="`pb-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td>{{ appt.CreatedAt | dateformat(dateFormatStr) }}</td>
                          <td>{{ appt.Employee }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                  <!--
                  <p class="fs-mini fw-semi-bold">Collected from Follow-ups:
                    <b-button id="pbscheduled-Monthly" variant="default" class="mb-xs mr-xs btn-xs">
                      <span
                        v-if="((patientBalancesScheduledDataMonthly && patientBalancesScheduledDataMonthly.length === 0) || (!patientBalancesScheduledDataMonthly))"
                        class="fs-mini fw-semi-bold">0</span>
                      <span
                        v-else-if="patientBalancesScheduledDataMonthly && patientBalancesScheduledDataMonthly.length > 0"
                        class="fs-mini fw-semi-bold">{{ patientBalancesScheduledDataMonthly.length }}</span>
                    </b-button>
                    {{ sumArrValue(patientBalancesScheduledDataMonthly, 'AppointmentValue') | money }}
                  </p>
                  <b-popover target="pbscheduled-Monthly" triggers="focus" placement="top"
                    v-if="patientBalancesScheduledDataMonthly && patientBalancesScheduledDataMonthly.length > 0">
                    <template v-slot:title><b>Appointment(s) Scheduled</b></template>
                    <table class="table">
                      <thead>
                        <tr>
                          <th>Patient</th>
                          <th>Appointment Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(appt, index) in patientBalancesScheduledDataMonthly"
                          :key="`pb-${appt.TeamioPatientID}-${index}`">
                          <td>{{ formatName(appt.Patient) }}</td>
                          <td><span class="text-success fw-semi-bold">{{ appt.AppointmentValue |
                              money(allSettings.GENERAL_CURRENCY_SYMBOL.PropertyValue) }}</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </b-popover>
                -->
                  <div class="chart-panel">
                    <InsightPieChart :chartdata="patientBalancesDataMonthly.chartData" :options="pieChartOptions" />
                  </div>
                </b-card>
                <b-card class="background-cover" v-else>
                  <div>
                    <img src="../../assets/notenoughdata-place-holder.png" alt="..." width="330" />
                  </div>
                </b-card>
              </div>
              <div class="col-sm-8"
                v-if="patientBalancesDataMonthlyByEmp && patientBalancesDataMonthlyByEmp.length > 0">
                <h5 class="section-title">Breakdown by Team member(s)</h5>
                <div class="team-member-grid">
                  <b-card class="background-cover team-member-card"
                    v-for="(employee, index) in patientBalancesDataMonthlyByEmp" :key="`employee-${index}`">
                    <div class="person-name">
                      <span class="glyphicon glyphicon-user mr-2"></span>
                      <div>
                        <h6>{{ employee.name }}</h6>
                        <p class="fw-semi-bold">{{ employee.totalCalls }} Follow-up(s)</p>
                      </div>
                    </div>
                    <div class="chart-panel">
                      <ChartJSBarChart :chartData="employee.chartData" :dateRange="formattedResultsStr"
                        :timeRange="timeRange" />
                    </div>
                  </b-card>
                </div>
              </div>
            </div>
          </div>
        </section>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import DatePicker from "vue2-datepicker";
import { mapState, mapActions, mapMutations } from 'vuex';
import moment from 'moment-timezone';
import _ from 'lodash';
import mixin from '../../mixin.js';
import InsightPieChart from '../TeamInsights/components/InsightPieChart.vue';
import ChartJSBarChart from '../TeamInsights/components/ChartJSBarChart.vue';
import CommonOverlay from '@/components/CommonOverlay.vue';

export default {
  name: 'TeamInsightsCustomReports',
  components: {
    DatePicker,
    InsightPieChart,
    ChartJSBarChart,
    CommonOverlay,
  },
  mixins: [mixin],
  data() {
    return {
      results: [],
      filtersApplied: false,
      busy: false,
      fromMonth: moment().subtract(3, 'months').toDate(),
      fromYear: moment().subtract(3, 'months').toDate(),
      toMonth: moment().toDate(),
      toYear: moment().toDate(),
      pieChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 20,
            padding: 10,
          },
        },
      },
      timeRange: 'month'
    };
  },
  computed: {
    // Format the date range for display
    formattedResultsStr() {
      return `Report: From ${moment(this.fromMonth).format('MMM')} ${moment(this.fromYear).format('YYYY')} to ${moment(this.toMonth).format('MMM')} ${moment(this.toYear).format('YYYY')}`;
    },

    // Overdue Recall Data
    overdueRecallDataMonthly() {
      if (this.overdueRecallStatuses && this.overdueRecallTasksDataMonthly) {
        let temp = _.clone(this.overdueRecallStatuses);
        let temp1 = _.merge(temp, _.countBy(this.overdueRecallTasksDataMonthly, 'Status'));
        return {
          totalCalls: _.sum(_.values(temp1)),
          chartData: this.makeChartData(temp1),
        };
      }
      return null;
    },

    overdueRecallScheduledDataMonthly() {
      if (this.insightsScheduledDataMonthly) {
        return _.uniqBy(this.insightsScheduledDataMonthly.filter((t) => t.TaskTypeID === 3 && t.AppointmentScheduled === 1), 'TeamioPatientID');
      }
      return [];
    },

    overdueRecallDataMonthlyByEmp() {
      if (this.overdueRecallTasksDataMonthly && this.overdueRecallStatuses && this.employees) {
        let ret = [];
        let callsByEmployee = _.groupBy(this.overdueRecallTasksDataMonthly, 'CreatedByEmpID');
        let employeeName;
        var _this = this;
        _.keys(callsByEmployee).forEach(function (k) {
          let c = _.countBy(callsByEmployee[k], 'Status');
          let employee = _this.employees.filter((e) => e.EmployeeID === k);
          if (employee.length > 0) {
            employeeName = employee[0].Name;
            let d = _.clone(_this.overdueRecallStatuses);
            _.merge(d, c);
            let allCallsByThisEmp = callsByEmployee[k].length;
            let allCallsScheduledByThisEmp = _this.overdueRecallScheduledDataMonthly.filter((c) => c.CreatedByEmpID === k);
            let allCallsScheduledByThisEmpLength = allCallsScheduledByThisEmp.length;
            let conversionRate = Math.ceil((allCallsScheduledByThisEmpLength / allCallsByThisEmp) * 100);
            let o = {
              name: employeeName,
              totalCalls: _.sum(_.values(d)),
              conversionRate: conversionRate,
              chartData: _this.makeChartDataMonthly(_this.overdueRecallTasksDataMonthly || [], allCallsScheduledByThisEmp || [], k, false, false, _this.multipleYearsInvolved),
            };
            ret.push(o);
          }
        });
        return ret;
      }
      return [];
    },

    // Unscheduled Treatment Data
    unscheduledTreatmentDataMonthly() {
      if (this.unscheduledTreatmentStatuses && this.unscheduledTreatmentTasksDataMonthly) {
        let temp = _.clone(this.unscheduledTreatmentStatuses);
        let temp1 = _.merge(temp, _.countBy(this.unscheduledTreatmentTasksDataMonthly, 'Status'));
        return {
          totalCalls: _.sum(_.values(temp1)),
          chartData: this.makeChartData(temp1),
        };
      }
      return null;
    },

    unscheduledTreatmentScheduledDataMonthly() {
      if (this.insightsScheduledDataMonthly) {
        return _.uniqBy(this.insightsScheduledDataMonthly.filter((t) => t.TaskTypeID === 4 && t.AppointmentScheduled === 1), 'TeamioPatientID');
      }
      return [];
    },

    unscheduledTreatmentDataMonthlyByEmp() {
      if (this.unscheduledTreatmentTasksDataMonthly && this.unscheduledTreatmentStatuses && this.employees) {
        let ret = [];
        let callsByEmployee = _.groupBy(this.unscheduledTreatmentTasksDataMonthly, 'CreatedByEmpID');
        let employeeName;
        var _this = this;
        _.keys(callsByEmployee).forEach(function (k) {
          let c = _.countBy(callsByEmployee[k], 'Status');
          let employee = _this.employees.filter((e) => e.EmployeeID === k);
          if (employee.length > 0) {
            employeeName = employee[0].Name;
            let d = _.clone(_this.unscheduledTreatmentStatuses);
            _.merge(d, c);
            let allCallsByThisEmp = callsByEmployee[k].length;
            let allCallsScheduledByThisEmp = _this.unscheduledTreatmentScheduledDataMonthly.filter((c) => c.CreatedByEmpID === k);
            let allCallsScheduledByThisEmpLength = allCallsScheduledByThisEmp.length;
            let conversionRate = Math.ceil((allCallsScheduledByThisEmpLength / allCallsByThisEmp) * 100);
            let o = {
              name: employeeName,
              totalCalls: _.sum(_.values(d)),
              conversionRate: conversionRate,
              chartData: _this.makeChartDataMonthly(_this.unscheduledTreatmentTasksDataMonthly || [], allCallsScheduledByThisEmp || [], k, false, false, _this.multipleYearsInvolved),
            };
            ret.push(o);
          }
        });
        return ret;
      }
      return [];
    },

    // Outstanding Claims Data
    outstandingClaimsDataMonthly() {
      if (this.outstandingClaimsStatuses && this.outstandingClaimsTasksDataMonthly) {
        let temp = _.clone(this.outstandingClaimsStatuses);
        let temp1 = _.merge(temp, _.countBy(this.outstandingClaimsTasksDataMonthly, 'Status'));
        return {
          totalCalls: _.sum(_.values(temp1)),
          chartData: this.makeChartData(temp1),
        };
      }
      return null;
    },

    outstandingClaimsClaimReceivedDataMonthly() {
      if (this.insightsScheduledDataMonthly) {
        return _.uniqBy(this.insightsScheduledDataMonthly.filter((t) => t.TaskTypeID === 2 && t.ClaimReceived === 1), 'ClaimNum');
      }
      return [];
    },

    outstandingClaimsDataMonthlyByEmp() {
      if (this.outstandingClaimsTasksDataMonthly && this.outstandingClaimsStatuses && this.employees) {
        let ret = [];
        let callsByEmployee = _.groupBy(this.outstandingClaimsTasksDataMonthly, 'CreatedByEmpID');
        let employeeName;
        var _this = this;
        _.keys(callsByEmployee).forEach(function (k) {
          let c = _.countBy(callsByEmployee[k], 'Status');
          let employee = _this.employees.filter((e) => e.EmployeeID === k);
          if (employee.length > 0) {
            employeeName = employee[0].Name;
            let d = _.clone(_this.outstandingClaimsStatuses);
            _.merge(d, c);
            let allCallsByThisEmp = callsByEmployee[k].length;
            let allCallsReceivedByThisEmp = _this.outstandingClaimsClaimReceivedDataMonthly.filter((c) => c.CreatedByEmpID === k);
            let allCallsReceivedByThisEmpLength = allCallsReceivedByThisEmp.length;
            let conversionRate = Math.ceil((allCallsReceivedByThisEmpLength / allCallsByThisEmp) * 100);
            let o = {
              name: employeeName,
              totalCalls: _.sum(_.values(d)),
              totalReceived: allCallsReceivedByThisEmpLength,
              conversionRate: conversionRate,
              chartData: _this.makeChartDataMonthly(_this.outstandingClaimsTasksDataMonthly || [], allCallsReceivedByThisEmp || [], k, true, false, _this.multipleYearsInvolved),
            };
            ret.push(o);
          }
        });
        return ret;
      }
      return [];
    },

    // Broken Appointment Data
    brokenAppointmentDataMonthly() {
      if (this.brokenAppointmentStatuses && this.brokenAppointmentTasksDataMonthly) {
        let temp = _.clone(this.brokenAppointmentStatuses);
        let temp1 = _.merge(temp, _.countBy(this.brokenAppointmentTasksDataMonthly, 'Status'));
        return {
          totalCalls: _.sum(_.values(temp1)),
          chartData: this.makeChartData(temp1),
        };
      }
      return null;
    },

    brokenAppointmentScheduledDataMonthly() {
      if (this.insightsScheduledDataMonthly) {
        return _.uniqBy(this.insightsScheduledDataMonthly.filter((t) => t.TaskTypeID === 8 && t.AppointmentScheduled === 1), 'TeamioPatientID');
      }
      return [];
    },

    brokenAppointmentDataMonthlyByEmp() {
      if (this.brokenAppointmentTasksDataMonthly && this.brokenAppointmentStatuses && this.employees) {
        let ret = [];
        let callsByEmployee = _.groupBy(this.brokenAppointmentTasksDataMonthly, 'CreatedByEmpID');
        let employeeName;
        var _this = this;
        _.keys(callsByEmployee).forEach(function (k) {
          let c = _.countBy(callsByEmployee[k], 'Status');
          let employee = _this.employees.filter((e) => e.EmployeeID === k);
          if (employee.length > 0) {
            employeeName = employee[0].Name;
            let d = _.clone(_this.brokenAppointmentStatuses);
            _.merge(d, c);
            let allCallsByThisEmp = callsByEmployee[k].length;
            let allCallsScheduledByThisEmp = _this.brokenAppointmentScheduledDataMonthly.filter((c) => c.CreatedByEmpID === k);
            let allCallsScheduledByThisEmpLength = allCallsScheduledByThisEmp.length;
            let conversionRate = Math.ceil((allCallsScheduledByThisEmpLength / allCallsByThisEmp) * 100);
            let o = {
              name: employeeName,
              totalCalls: _.sum(_.values(d)),
              conversionRate: conversionRate,
              chartData: _this.makeChartDataMonthly(_this.brokenAppointmentTasksDataMonthly || [], allCallsScheduledByThisEmp || [], k, false, false, _this.multipleYearsInvolved),
            };
            ret.push(o);
          }
        });
        return ret;
      }
      return [];
    },

    // Pre-authorizations Data
    preAuthorizationDataMonthly() {
      if (this.preAuthorizationStatuses && this.preAuthorizationTasksDataMonthly) {
        let temp = _.clone(this.preAuthorizationStatuses);
        let temp1 = _.merge(temp, _.countBy(this.preAuthorizationTasksDataMonthly, 'Status'));
        return {
          totalCalls: _.sum(_.values(temp1)),
          chartData: this.makeChartData(temp1),
        };
      }
      return null;
    },

    preAuthorizationScheduledDataMonthly() {
      if (this.insightsScheduledDataMonthly) {
        return _.uniqBy(this.insightsScheduledDataMonthly.filter((t) => t.TaskTypeID === 5 && t.AppointmentScheduled === 1), 'TeamioPatientID');
      }
      return [];
    },

    preAuthorizationDataMonthlyByEmp() {
      if (this.preAuthorizationTasksDataMonthly && this.preAuthorizationStatuses && this.employees) {
        let ret = [];
        let callsByEmployee = _.groupBy(this.preAuthorizationTasksDataMonthly, 'CreatedByEmpID');
        let employeeName;
        var _this = this;
        _.keys(callsByEmployee).forEach(function (k) {
          let c = _.countBy(callsByEmployee[k], 'Status');
          let employee = _this.employees.filter((e) => e.EmployeeID === k);
          if (employee.length > 0) {
            employeeName = employee[0].Name;
            let d = _.clone(_this.preAuthorizationStatuses);
            _.merge(d, c);
            let allCallsByThisEmp = callsByEmployee[k].length;
            let allCallsScheduledByThisEmp = _this.preAuthorizationScheduledDataMonthly.filter((c) => c.CreatedByEmpID === k);
            let allCallsScheduledByThisEmpLength = allCallsScheduledByThisEmp.length;
            let conversionRate = Math.ceil((allCallsScheduledByThisEmpLength / allCallsByThisEmp) * 100);
            let o = {
              name: employeeName,
              totalCalls: _.sum(_.values(d)),
              conversionRate: conversionRate,
              chartData: _this.makeChartDataMonthly(_this.preAuthorizationTasksDataMonthly || [], allCallsScheduledByThisEmp || [], k, false, false, _this.multipleYearsInvolved),
            };
            ret.push(o);
          }
        });
        return ret;
      }
      return [];
    },

    // Patient Balances Data
    patientBalancesDataMonthly() {
      if (this.patientBalancesStatuses && this.patientBalancesTasksDataMonthly) {
        let temp = _.clone(this.patientBalancesStatuses);
        let temp1 = _.merge(temp, _.countBy(this.patientBalancesTasksDataMonthly, 'Status'));
        return {
          totalCalls: _.sum(_.values(temp1)),
          chartData: this.makeChartData(temp1),
        };
      }
      return null;
    },

    /*
    patientBalancesScheduledDataMonthly() {
      if (this.insightsScheduledDataMonthly) {
        return _.uniqBy(this.insightsScheduledDataMonthly.filter((t) => t.TaskTypeID === 7 && t.AppointmentScheduled === 1), 'TeamioPatientID');
      }
      return [];
    },
    */

    patientBalancesDataMonthlyByEmp() {
      if (this.patientBalancesTasksDataMonthly && this.patientBalancesStatuses && this.employees) {
        let ret = [];
        let callsByEmployee = _.groupBy(this.patientBalancesTasksDataMonthly, 'CreatedByEmpID');
        let employeeName;
        var _this = this;
        _.keys(callsByEmployee).forEach(function (k) {
          let c = _.countBy(callsByEmployee[k], 'Status');
          let employee = _this.employees.filter((e) => e.EmployeeID === k);
          if (employee.length > 0) {
            employeeName = employee[0].Name;
            let d = _.clone(_this.patientBalancesStatuses);
            _.merge(d, c);
            let allCallsByThisEmp = callsByEmployee[k].length;
            let allCallsScheduledByThisEmp = null;
            let allCallsScheduledByThisEmpLength = 0;
            let conversionRate = 0;
            //let allCallsScheduledByThisEmp = _this.patientBalancesScheduledDataMonthly.filter((c) => c.CreatedByEmpID === k);
            //let allCallsScheduledByThisEmpLength = allCallsScheduledByThisEmp.length;
            //let conversionRate = Math.ceil((allCallsScheduledByThisEmpLength / allCallsByThisEmp) * 100);
            let o = {
              name: employeeName,
              totalCalls: _.sum(_.values(d)),
              conversionRate: conversionRate,
              chartData: _this.makeChartDataMonthly(_this.patientBalancesTasksDataMonthly || [], allCallsScheduledByThisEmp || [], k, false, false, _this.multipleYearsInvolved),
            };
            ret.push(o);
          }
        });
        return ret;
      }
      return [];
    },

    // Other computed properties
    overdueRecallTasksDataMonthly() {
      if (this.insightsDataMonthly) return this.insightsDataMonthly.filter((t) => t.TaskTypeID === 3);
      return []; // Return an empty array if insightsDataMonthly is not defined
    },

    unscheduledTreatmentTasksDataMonthly() {
      if (this.insightsDataMonthly) return this.insightsDataMonthly.filter((t) => t.TaskTypeID === 4);
      return []; // Return an empty array if insightsDataMonthly is not defined
    },

    outstandingClaimsTasksDataMonthly() {
      if (this.insightsDataMonthly) return this.insightsDataMonthly.filter((t) => t.TaskTypeID === 2);
      return []; // Return an empty array if insightsDataMonthly is not defined
    },

    brokenAppointmentTasksDataMonthly() {
      if (this.insightsDataMonthly) return this.insightsDataMonthly.filter((t) => t.TaskTypeID === 8);
      return []; // Return an empty array if insightsDataMonthly is not defined
    },

    preAuthorizationTasksDataMonthly() {
      if (this.insightsDataMonthly) return this.insightsDataMonthly.filter((t) => t.TaskTypeID === 5);
      return []; // Return an empty array if insightsDataMonthly is not defined
    },

    patientBalancesTasksDataMonthly() {
      if (this.insightsDataMonthly) return this.insightsDataMonthly.filter((t) => t.TaskTypeID === 7);
      return []; // Return an empty array if insightsDataMonthly is not defined
    },

    overdueRecallStatuses() {
      if (this.siteStatuses) {
        let statuses = _.map(this.siteStatuses.filter((s) => s.TaskTypeID === 3), 'ClientSystemFollowupStatus');
        let obj = {};
        _.map(statuses, function (val) {
          obj[val] = 0;
        });
        return obj;
      }
      return {};
    },

    unscheduledTreatmentStatuses() {
      if (this.siteStatuses) {
        let statuses = _.map(this.siteStatuses.filter((s) => s.TaskTypeID === 4), 'ClientSystemFollowupStatus');
        let obj = {};
        _.map(statuses, function (val) {
          obj[val] = 0;
        });
        return obj;
      }
      return {};
    },

    outstandingClaimsStatuses() {
      if (this.siteStatuses) {
        let statuses = _.map(this.siteStatuses.filter((s) => s.TaskTypeID === 2), 'ClientSystemFollowupStatus');
        let obj = {};
        _.map(statuses, function (val) {
          obj[val] = 0;
        });
        return obj;
      }
      return {};
    },

    brokenAppointmentStatuses() {
      if (this.siteStatuses) {
        let statuses = _.map(this.siteStatuses.filter((s) => s.TaskTypeID === 8), 'ClientSystemFollowupStatus');
        let obj = {};
        _.map(statuses, function (val) {
          obj[val] = 0;
        });
        return obj;
      }
      return {};
    },

    preAuthorizationStatuses() {
      if (this.siteStatuses) {
        let statuses = _.map(this.siteStatuses.filter((s) => s.TaskTypeID === 5), 'ClientSystemFollowupStatus');
        let obj = {};
        _.map(statuses, function (val) {
          obj[val] = 0;
        });
        return obj;
      }
      return {};
    },

    patientBalancesStatuses() {
      if (this.siteStatuses) {
        let statuses = _.map(this.siteStatuses.filter((s) => s.TaskTypeID === 7), 'ClientSystemFollowupStatus');
        let obj = {};
        _.map(statuses, function (val) {
          obj[val] = 0;
        });
        return obj;
      }
      return {};
    },

    insightsScheduledDataMonthly() {
      if (this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[1]) {
        return this.dataToDisplay[2];
      }
      return [];
    },

    insightsDataMonthly() {
      if (this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[1]) {
        return this.dataToDisplay[0];
      }
      return [];
    },

    siteStatuses() {
      if (this.dataToDisplay && this.dataToDisplay.length > 0 && this.dataToDisplay[1]) {
        return this.dataToDisplay[1];
      }
      return [];
    },

    dataToDisplay() {
      if (this.results && this.results.data && Array.isArray(this.results.data)) return this.results.data;
      return []; // Return an empty array if results.data is not defined or not an array
    },
  },
  methods: {
    ...mapActions('teamio', ['runQuery']),
    ...mapMutations('teamio', ['emptyResults']),
    async init() {
      this.resetError();
    },

    async applyFilter() {
      this.resetError();
      
      // Validate date picker values before processing
      if (!this.fromMonth || !this.fromYear || !this.toMonth || !this.toYear) {
        this.$toast.error('Please select valid dates for both From and To fields');
        return;
      }
      
      // Safely extract date components with validation
      const fromMoment = moment(this.fromMonth);
      const fromYearMoment = moment(this.fromYear);
      const toMoment = moment(this.toMonth);
      const toYearMoment = moment(this.toYear);
      
      // Validate moment objects
      if (!fromMoment.isValid() || !fromYearMoment.isValid() || !toMoment.isValid() || !toYearMoment.isValid()) {
        this.$toast.error('Invalid date selection. Please choose valid dates.');
        return;
      }
      
      let fm = fromMoment.month() + 1;
      let fy = fromYearMoment.year();
      let tm = toMoment.month() + 1;
      let ty = toYearMoment.year();
      
      // Additional validation for extracted values
      if (isNaN(fm) || isNaN(fy) || isNaN(tm) || isNaN(ty)) {
        this.$toast.error('Date processing error. Please refresh the page and try again.');
        return;
      }
      
      let daysInMonthTo = moment(`${ty}-${tm}-01`).daysInMonth();
      let fromDate = `${fy}-${String(fm).padStart(2, '0')}-01`;
      let toDate = `${ty}-${String(tm).padStart(2, '0')}-${String(daysInMonthTo).padStart(2, '0')}`;

      if ((fy === ty && tm < fm) || ty > moment().year() || fy > ty) {
        this.$toast.error('Your date range is invalid. Please try again');
        return;
      }

      // Check if date range exceeds 3 years and spans multiple years
      const fromDateMoment = moment(fromDate);
      const toDateMoment = moment(toDate);
      const yearsDifference = toDateMoment.diff(fromDateMoment, 'years', true);
      
      if (yearsDifference > 3) {
        this.$toast.error('Date range cannot exceed 3 years. Please select a shorter time period for better performance.');
        return;
      }

      const multipleYearsInvolved = fromDateMoment.year() !== toDateMoment.year();

      let query = {
        type: 'CUSTOM_TEAM_INSIGHTS_REPORT',
        siteID: this.siteID,
        from: `${fromDate}`,
        to: `${toDate}`,
        d: moment().format('YYYY-MM-DD'),
      };

      //console.log('payload', query);

      this.emptyResults();
      this.busy = true; // Show loading overlay
      try {
        this.results = await this.runQuery(query);
        this.filtersApplied = true;
        this.multipleYearsInvolved = multipleYearsInvolved; // Set the flag
      } catch (error) {
        console.error('Error fetching custom reports:', error);
        
        // Check if it's a response size issue
        const errorMessage = error?.message || error?.toString() || '';
        const isResponseSizeError = errorMessage.includes('too large') || 
                                   errorMessage.includes('size limit') || 
                                   errorMessage.includes('payload') ||
                                   errorMessage.includes('ResponseSizeTooLarge') ||
                                   errorMessage.includes('truncated') ||
                                   error?.response?.status === 413;
        
        if (isResponseSizeError) {
          this.$toast.error('The selected date range contains too much data to display. Please try selecting a shorter time period (e.g., 6-12 months) for better performance.');
        } else {
          this.$toast.error('Failed to load reports. Please try again.');
        }
      } finally {
        this.busy = false; // Hide loading overlay
      }
    },
    
    // Helper function to get colors from the Huddle-style palette
    getChartColor(index) {
        // Teamio Huddle-style color palette - consistent across all charts
        const colors = [
          '#3abf94',  // Primary green (brand)
          '#3b82f6',  // Blue
          '#8b5cf6',  // Purple
          '#f59e0b',  // Amber
          '#64748b',  // Slate
          '#06b6d4',  // Cyan
          '#ec4899',  // Pink
          '#10b981',  // Emerald
          '#6366f1',  // Indigo
          '#78716c',  // Stone
        ];
        return colors[index % colors.length];
    },
    determineTimeRange(data) {
      // Ensure data is an array
      if (!Array.isArray(data) || data.length === 0) return 'day'; // Default to days if no data

      const dates = data.map(item => moment(item.CreatedAt));
      const minDate = moment.min(dates);
      const maxDate = moment.max(dates);

      const diffInDays = maxDate.diff(minDate, 'days');
      const diffInWeeks = maxDate.diff(minDate, 'weeks');
      const diffInMonths = maxDate.diff(minDate, 'months');

      if (diffInMonths > 1) {
        return 'month'; // Use month names if data spans multiple months
      } else if (diffInWeeks > 1) {
        return 'week'; // Use week names if data spans multiple weeks
      } else {
        return 'day'; // Use day names if data is within a week
      }
    },
    makeChartData(d) {
        // Extract labels and data from the object d
        const labels = Object.keys(d);
        const data = Object.values(d);

        // Return the data formatted for a pie chart
        return {
            labels: labels, // X-axis labels
            datasets: [
                {
                    data: data, // Y-axis values
                    backgroundColor: labels.map((_, index) => this.getChartColor(index)), // Assign colors by index for consistency
                }
            ]
        };
    },

    makeChartDataMonthly(tasksData, _scheduledData, k, claimBased, paymentBased, multipleYearsInvolved = false, funcName = '') {
      //console.log('tasksData', tasksData);
      const timeRange = this.timeRange; // Use the pre-calculated timeRange

      let callsByEmployee = _.groupBy(tasksData, 'CreatedByEmpID');
      let scheduledByEmployee = _.groupBy(_scheduledData, 'CreatedByEmpID');

      // Group calls by month (and year if multiple years are involved)
      let callsByTime = _.groupBy(callsByEmployee[k], (o) => {
        if (timeRange === 'month') {
          if (multipleYearsInvolved) {
            return `${moment(o.CreatedAt).format('MMM')}-${moment(o.CreatedAt).format('YY')}`; // Format: "Oct-24"
          } else {
            return moment(o.CreatedAt).format('MMM'); // Format: "Oct"
          }
        } else if (timeRange === 'week') {
          return `Week ${moment(o.CreatedAt).week()}`; // Week names
        } else {
          return moment(o.CreatedAt).format('ddd'); // Day names
        }
      });

      // Group scheduled data by month (and year if multiple years are involved)
      let scheduledByTime = _.groupBy(scheduledByEmployee[k], (o) => {
        if (timeRange === 'month') {
          if (multipleYearsInvolved) {
            return `${moment(o.UpdatedAt).format('MMM')}-${moment(o.UpdatedAt).format('YY')}`; // Format: "Oct-24"
          } else {
            return moment(o.UpdatedAt).format('MMM'); // Format: "Oct"
          }
        } else if (timeRange === 'week') {
          return `Week ${moment(o.UpdatedAt).week()}`; // Week names
        } else {
          return moment(o.UpdatedAt).format('ddd'); // Day names
        }
      });

      // Get all unique month (or month-year) labels from the data
      const allMonths = Object.keys(callsByTime).concat(Object.keys(scheduledByTime));
      const uniqueMonths = _.uniq(allMonths);

      // Sort month (or month-year) labels in chronological order
      const sortedMonths = uniqueMonths.sort((a, b) => {
        const format = multipleYearsInvolved ? 'MMM-YY' : 'MMM'; // Use the correct format for sorting
        const dateA = moment(a, format); // Parse "Oct-24" or "Oct"
        const dateB = moment(b, format); // Parse "Oct-24" or "Oct"
        return dateA.valueOf() - dateB.valueOf(); // Sort by timestamp
      });

      // Generate labels and data in the correct order
      let labels = sortedMonths; // X-axis labels (sorted month or month-year)
      let callData = labels.map((monthLabel) => callsByTime[monthLabel] ? callsByTime[monthLabel].length : 0); // Y-axis values for calls
      let scheduledData = labels.map((monthLabel) => scheduledByTime[monthLabel] ? scheduledByTime[monthLabel].length : 0); // Y-axis values for scheduled

      /* 
        now I need a way to know if the data is related to Outstanding Claims or Pre-auth (taskTypeID 2 or 5) if so then Lable should be "Received" and not "Called"
        if the taskTypeID = 6 (patient balances) then lable should be "Paid"
      */

      // Extract TaskTypeID and remove duplicates
      const distinctTaskTypeIDs = [...new Set(tasksData.map(item => item.TaskTypeID))];
      //console.log('distinctTaskTypeIDs', distinctTaskTypeIDs);

      let label;
      
      let whichTaskType = distinctTaskTypeIDs[0];

      switch(whichTaskType) {
        case 2: 
        case 5: label = 'Received'; break;
        case 7: label = '';  break;
        default: label = 'Scheduled'; break;
      }
      
      //console.log('whichTaskType', whichTaskType);
      //console.log('label', label);

      //for Patient balances, we're not showing "scheduled/paid data", so we can skip that from the dataset
      if (whichTaskType == 7){
        return {
        labels: labels, // X-axis labels (sorted month or month-year)
        datasets: [
          {
            label: "Call", // Legend label for calls
            backgroundColor: "#3abf94", // Primary green - teamio theme
            data: callData, // Y-axis values for calls
          },


        ],
      };

      }

      else { //for all other task types

        return {
          labels: labels, // X-axis labels (sorted month or month-year)
          datasets: [
            {
              label: "Call", // Legend label for calls
              backgroundColor: "#3abf94", // Primary green - teamio theme
              data: callData, // Y-axis values for calls
            },

            {
              label: label, // Legend label for scheduled
              backgroundColor: "#3b82f6", // Blue - contrasts well with green
              data: scheduledData, // Y-axis values for scheduled
            },

          ],
        };

    }


    },
   
  },
};
</script>

<style lang="scss" scoped>
// =====================================================
// TEAM INSIGHTS CUSTOM REPORTS - Modern styling matching TeamInsights
// =====================================================
.team-tab {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  padding: 1rem 1.5rem;
  min-height: 100vh;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 16px 0;
  line-height: 1.3;
}

.report-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 16px;
}

.section-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e2e8f0;
}

// Date filter card
::v-deep .b-card,
::v-deep .card {
  background-color: white !important;
  border-radius: 10px !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #e2e8f0 !important;
}

// Info sections
.info-section {
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  padding: 16px 20px;
  margin-bottom: 16px;

  .row {
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
  }

  .border-right {
    border-right: 1px solid #e2e8f0 !important;
    padding-right: 24px;

    @media (max-width: 780px) {
      border-right: none !important;
      border-bottom: 1px solid #e2e8f0 !important;
      padding-right: 12px;
      padding-bottom: 20px;
      margin-bottom: 20px;
    }
  }
}

// Team member grid
.team-member-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
  padding: 0;
}

.team-member-card {
  min-height: 320px;
  background-color: #fafbfc !important;
  border-radius: 10px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3abf94 !important;
    box-shadow: 0 4px 12px rgba(58, 191, 148, 0.1) !important;
  }

  ::v-deep .card-body {
    padding: 14px !important;
  }
}

// Person name styling
.person-name {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;

  .glyphicon-user {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(58, 191, 148, 0.15) 0%, rgba(58, 191, 148, 0.08) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3abf94;
    font-size: 0.875rem;
  }

  h6 {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: #0f172a;
  }

  p {
    margin: 0;
    font-size: 0.75rem;
    color: #64748b;
  }
}

// Chart panels
.chart-panel {
  min-height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  padding: 10px 0;

  > div {
    width: 100% !important;
    height: 280px !important;
  }

  canvas {
    max-width: 100%;
  }
}

// Empty state styling
.background-cover {
  img {
    display: none;
  }

  &:has(img) {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 24px;
    min-height: 200px;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border: 2px dashed #e2e8f0 !important;
    border-radius: 10px !important;

    &::before {
      content: '\f080';
      font-family: 'FontAwesome';
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(58, 191, 148, 0.15) 0%, rgba(58, 191, 148, 0.08) 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.75rem;
      color: #3abf94;
      margin-bottom: 16px;
    }

    &::after {
      content: 'No Data Available';
      font-size: 1rem;
      font-weight: 600;
      color: #334155;
    }
  }
}

// Stats buttons
.btn-xs,
.btn.btn-xs {
  padding: 3px 8px;
  font-size: 0.75rem;
  border-radius: 5px;
  background-color: rgba(58, 191, 148, 0.1) !important;
  border: 1px solid rgba(58, 191, 148, 0.3) !important;
  color: #2d9a77 !important;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover,
  &:focus {
    background-color: rgba(58, 191, 148, 0.2) !important;
    border-color: #3abf94 !important;
  }

  span {
    color: inherit !important;
    font-size: 0.75rem !important;
  }
}

// Text utilities
.fs-mini {
  font-size: 0.8125rem;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 4px;

  &.fw-semi-bold {
    font-weight: 500;
  }
}

.text-success {
  color: #16a34a !important;
}

// Date filter styling
.date-filter-container {
  margin-bottom: 16px;
}

.date-filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-label {
  font-size: 0.8125rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
}

.date-inputs {
  display: flex;
  gap: 8px;
}

.date-picker-wrapper {
  flex: 1;

  ::v-deep .mx-datepicker {
    width: 100%;

    .mx-input {
      height: 36px;
      border-radius: 6px;
      border: 1px solid #e2e8f0;
      font-size: 0.8125rem;
      padding: 0 10px;

      &:focus {
        border-color: #3abf94;
        box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.1);
      }
    }
  }
}

.submit-button {
  display: flex;
  justify-content: flex-start;

  .btn-primary {
    background-color: #3abf94 !important;
    border-color: #3abf94 !important;
    font-size: 0.8125rem;
    font-weight: 500;
    padding: 8px 16px;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
      background-color: #2d9a77 !important;
      border-color: #2d9a77 !important;
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .team-tab {
    padding: 0.75rem;
  }

  .page-title {
    font-size: 1.375rem;
  }

  .date-filter-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }

  .date-filter {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
    width: 100%;
  }

  .date-inputs {
    width: 100%;
  }

  .submit-button {
    width: 100%;
  }

  .team-member-grid {
    grid-template-columns: 1fr;
  }
}
</style>